// Centralized auth/token handling: short-lived access token + longer-lived refresh
// token, with silent background renewal and cross-tab logout sync.
import { useToast } from 'vue-toastification';

const ACCESS_TOKEN_KEY = 'ChatNavHub_access_token';
const REFRESH_TOKEN_KEY = 'ChatNavHub_refresh_token';
const API_BASE = process.env.VUE_APP_API_BASE_URL || '';

const REFRESH_SKEW_MS = 60 * 1000; // renew 60s before the access token actually expires
const RETRY_DELAY_MS = 30 * 1000; // retry after a network error

let router = null;
let refreshTimer = null;
let refreshInFlight = null;

export function setRouter(r) {
  router = r;
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function decodeToken(token) {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch (e) {
    return null;
  }
}

export function isTokenExpired(token) {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

export function setTokens(accessToken, refreshToken) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  scheduleAutoRefresh();
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

// Explicit, user-initiated or forced logout. Redirects to the login page and,
// if a message is given, explains why (e.g. session expired).
export function logout(message) {
  const hadSession = !!getAccessToken();
  clearTokens();
  if (hadSession && message) {
    useToast().error(message, { timeout: false });
  }
  if (router && router.currentRoute.value.path !== '/') {
    router.push('/');
  }
}

// Exchanges the refresh token for a new access/refresh token pair.
// Returns the new access token, or null if renewal failed (in which case,
// unless it was a plain network error, the user has already been logged out).
export async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken || isTokenExpired(refreshToken)) {
    logout('Your session has expired. Please log in again.');
    return null;
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      if (!response.ok) {
        logout('Your session has expired. Please log in again.');
        return null;
      }
      const data = await response.json();
      setTokens(data.access_token, data.refresh_token);
      return data.access_token;
    } catch (e) {
      return null; // network hiccup - leave the session as-is, caller/timer will retry
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function attemptScheduledRefresh() {
  const tokenBeforeAttempt = getAccessToken();
  const newToken = await refreshAccessToken();
  if (!newToken && getAccessToken() === tokenBeforeAttempt) {
    // Failed without logging out (network error) - try again shortly.
    refreshTimer = setTimeout(attemptScheduledRefresh, RETRY_DELAY_MS);
  }
}

function scheduleAutoRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  const payload = decodeToken(getAccessToken());
  if (!payload || !payload.exp) return;

  const delay = Math.max(payload.exp * 1000 - Date.now() - REFRESH_SKEW_MS, 0);
  refreshTimer = setTimeout(attemptScheduledRefresh, delay);
}

// Keep every open tab in sync: if the session is cleared or renewed in one tab
// (logout, or a silent refresh), reflect that here too instead of relying on
// the user to eventually make an API call or navigate.
window.addEventListener('storage', (event) => {
  if (event.key !== ACCESS_TOKEN_KEY) return;
  if (!event.newValue) {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    if (router && router.currentRoute.value.path !== '/') {
      router.push('/');
    }
  } else {
    scheduleAutoRefresh();
  }
});

// Resume the refresh schedule on load (e.g. page refresh with an existing session).
if (getAccessToken()) {
  scheduleAutoRefresh();
}
